import { useState, useRef, useEffect } from 'react';
import { MdEdit, MdVisibility, MdMoreVert, MdHelp, MdImage, MdCode, MdSave } from 'react-icons/md';
import ImageUploadToolbar from './ImageUpload/ImageUploadToolbar';

const DocumentHeader = ({
  activeTab,
  handleTabSwitch,
  onShowQuickReference,
  onImageUpload = null,
  imageUploadDisabled = false,
  onInsertCodeBlock = null,
  textAreaRef = null,
  onSave = null,
  isSaving = false
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);
  const dropdownRef = useRef(null);
  const headerRef = useRef(null);

  // Handle inserting code block
  const handleInsertCodeBlock = () => {
    if (!onInsertCodeBlock || !textAreaRef?.current) return;

    const textarea = textAreaRef.current;
    const cursorPos = textarea.selectionStart;
    const scrollTop = textarea.scrollTop;

    // Call the parent's callback
    onInsertCodeBlock();

    // Preserve scroll position after insertion
    setTimeout(() => {
      textarea.scrollTop = scrollTop;
    }, 10);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  // Detect when column is narrow using ResizeObserver
  useEffect(() => {
    const handleResize = () => {
      if (headerRef.current) {
        const width = headerRef.current.offsetWidth;
        setIsNarrow(width < 32 * 16); // 32rem in pixels (16px = 1rem) - reduced for better spacing
      }
    };

    if (headerRef.current) {
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(headerRef.current);
      handleResize(); // Initial check

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, []);

  return (
    <div ref={headerRef} className="h-16 bg-white border-b border-gray-200/60 flex items-center px-4 sm:px-6 gap-4 flex-shrink-0">
      {/* Left: Tab Navigation */}
      <div className="flex items-center flex-shrink-0">
        <div className="flex items-center bg-gray-100/70 rounded-lg p-0.5">
          <button
            onClick={() => handleTabSwitch('markdown')}
            className={`relative px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
              activeTab === 'markdown'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-800 hover:bg-white/50'
            }`}
          >
            <MdEdit size={16} className="inline mr-2" />
            Write
          </button>
          <button
            onClick={() => handleTabSwitch('preview')}
            className={`relative px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
              activeTab === 'preview'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-800 hover:bg-white/50'
            }`}
          >
            <MdVisibility size={16} className="inline mr-2" />
            Preview
          </button>
        </div>
      </div>

      {/* Spacer */}
      <div className="flex-1"></div>
          
      {/* Right: Actions */}
      <div className="flex items-center space-x-2 flex-shrink-0 min-w-0">
        
        {!isNarrow ? (
          <>
            {/* Individual buttons when there's space */}
            {activeTab === 'markdown' && onImageUpload && (
              <ImageUploadToolbar
                onImageUpload={onImageUpload}
                disabled={imageUploadDisabled}
              />
            )}
            {activeTab === 'markdown' && onInsertCodeBlock && (
              <button
                onClick={handleInsertCodeBlock}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 bg-white border border-gray-200 hover:border-blue-200 rounded-lg transition-all duration-200"
                title="Insert Runnable Code Block"
              >
                <MdCode size={16} />
                <span>Code</span>
              </button>
            )}
            {onSave && (
              <button
                onClick={onSave}
                disabled={isSaving}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 rounded-lg transition-colors duration-200"
                title="Save document"
              >
                <MdSave size={16} />
                <span>{isSaving ? 'Saving…' : 'Save'}</span>
              </button>
            )}
            <button
              onClick={onShowQuickReference}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100/60 rounded-lg transition-colors duration-200"
            >
              <MdHelp size={16} />
              <span>Help</span>
            </button>
          </>
        ) : (
          /* Dropdown menu when narrow */
          <div className="relative" ref={dropdownRef}>
            <button 
              onClick={() => setShowDropdown(!showDropdown)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100/60 rounded-lg transition-colors duration-200"
            >
              <MdMoreVert size={20} />
            </button>
            
            {/* Dropdown Menu */}
            {showDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200/60 rounded-xl shadow-xl z-50 py-2">
                {activeTab === 'markdown' && onImageUpload && (
                  <button
                    onClick={() => {
                      // Trigger the file input from ImageUploadToolbar
                      const fileInput = document.createElement('input');
                      fileInput.type = 'file';
                      fileInput.accept = 'image/*';
                      fileInput.onchange = (e) => {
                        const file = e.target.files?.[0];
                        if (file && onImageUpload) {
                          onImageUpload(file);
                        }
                      };
                      fileInput.click();
                      setShowDropdown(false);
                    }}
                    disabled={imageUploadDisabled}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 text-sm transition-colors duration-200 ${
                      imageUploadDisabled
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <MdImage size={16} />
                    <span>Upload Image</span>
                  </button>
                )}
                {activeTab === 'markdown' && onInsertCodeBlock && (
                  <button
                    onClick={() => {
                      handleInsertCodeBlock();
                      setShowDropdown(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                  >
                    <MdCode size={16} />
                    <span>Insert Code Block</span>
                  </button>
                )}
                {onSave && (
                  <button
                    onClick={() => { onSave(); setShowDropdown(false); }}
                    disabled={isSaving}
                    className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-indigo-700 hover:bg-indigo-50 transition-colors duration-200 disabled:opacity-60"
                  >
                    <MdSave size={16} />
                    <span>{isSaving ? 'Saving…' : 'Save'}</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    onShowQuickReference();
                    setShowDropdown(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  <MdHelp size={16} />
                  <span>Quick Reference</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentHeader;